/**
 * ============================================================================
 * WelcomePage.js - Association & Referral Welcome Page
 * ============================================================================
 * Supports two URL patterns:
 *   /welcome/:slug   → Association member benefit (e.g., /welcome/gcga)
 *   /welcome?ref=CODE → Personal referral link
 * 
 * Changes from v1:
 * - Slug-based routing for association partnerships
 * - No "Founders" branding — positioned as exclusive member benefit
 * - Much more prominent demo/features section before CTA
 * - Association-specific messaging and personalization
 * - Configurable association registry (easy to add new partners)
 * 
 * Created: February 2, 2026
 * Updated: February 8, 2026 - Association partnership support
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// ============================================================================
// ASSOCIATION REGISTRY
// Add new association partnerships here. Each entry creates a dedicated
// welcome page at /welcome/{slug}
// ============================================================================
const ASSOCIATIONS = {
  // No active association partnerships. To add one, insert an entry keyed by slug:
  //   gcga: { name, shortName, description, price: '$49.99/year', tagline }
  // It will render at /welcome/{slug}. Referral links (/welcome?ref=CODE) are unaffected.
};

// ============================================================================
// DEFAULT CONFIG (for /welcome?ref=CODE personal referrals)
// ============================================================================
const DEFAULT_CONFIG = {
  name: null,
  shortName: null,
  description: null,
  price: '$49.99/year',
  tagline: 'Exclusive Invitation Rate',
  isReferral: true,
};

const WelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug } = useParams(); // from /welcome/:slug route
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foundersActive, setFoundersActive] = useState(true);
  const [foundersCount, setFoundersCount] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  // Determine if this is an association page or a referral page
  const association = slug ? ASSOCIATIONS[slug.toLowerCase()] : null;
  const config = association || DEFAULT_CONFIG;
  const isAssociation = !!association;

  // Get referral code from URL (for personal referrals)
  const refCode = searchParams.get('ref') || '';
  const plan = searchParams.get('plan') || 'free';

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track click and get referrer info (only for personal referrals)
  useEffect(() => {
    if (refCode && !isAssociation) {
      fetch(`${API_URL}/referral/track-click?code=${encodeURIComponent(refCode.toUpperCase())}`)
        .then(res => res.json())
        .then(data => {
          setReferrerInfo(data.referrerInfo);
          setFoundersActive(data.foundersActive);
          setFoundersCount(data.foundersCount || 0);
          setLoading(false);
        })
        .catch(err => {
          console.log('Referral lookup failed:', err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [refCode, isAssociation]);

  // Auto-rotate screenshots
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveScreenshot(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Colors
  const colors = {
    green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
    green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
    gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
    gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
    gray100: '#f3f4f6', gray50: '#f9fafb',
    amber500: '#f59e0b', amber400: '#fbbf24', amber300: '#fcd34d',
    amber100: '#fef3c7', amber50: '#fffbeb',
  };

  const handleGetStarted = () => {
    const params = new URLSearchParams();
    if (refCode) params.set('ref', refCode);
    if (slug) params.set('partner', slug);
    params.set('plan', plan || 'free');
    params.set('from', 'welcome');
    navigate(`/register?${params.toString()}`);
  };

  const handleViewDemo = () => {
    navigate('/#try-demo');
  };

  const handleLearnMore = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  // Feature categories for the expanded section
  const featureCategories = [
    {
      title: 'For the Organizer',
      icon: '🏌️',
      features: [
        'Create & publish tee times in under 30 seconds',
        'Automatic email & text reminders with weather forecasts',
        'Smart pairings & flights — balanced automatically or set by hand, ready to print',
        'Live scoring during the round, with digital scorecards',
        'Track RSVPs, waitlists, and cancellations automatically',
        'Message your whole group or individual players',
      ],
    },
    {
      title: 'For the Players',
      icon: '⛳',
      features: [
        'One-tap RSVP from their phone — no app download required',
        'See available tee times with weather and course info',
        'Course handicap figured automatically for the tees they\'re playing',
        'Keep score live and get a digital scorecard after the round',
        'Personal stats and round history that build over time',
        'Celebration alerts for birdies and standout holes as they happen',
      ],
    },
  ];

  // How it works steps
  const howItWorks = [
    { step: '1', title: 'Create a tee time', desc: 'Pick the date, time, course, and number of spots. Takes about 30 seconds.' },
    { step: '2', title: 'Publish to your group', desc: 'Players get an email or text with the details and a one-tap link to RSVP.' },
    { step: '3', title: 'Players grab a spot', desc: 'One tap on their phone. No account needed. Waitlist fills automatically if it\'s full.' },
    { step: '4', title: 'Play and keep score', desc: 'Reminders with weather go out, the tee sheet\'s ready to print, and players can score the round right from their phone.' },
  ];

  // Referrer display name (for personal referrals only)
  const referrerName = referrerInfo?.name || 'A fellow golfer';
  const referrerOrg = referrerInfo?.organizationName;

  return (
    <div style={{ 
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      minHeight: '100vh',
      background: 'white',
    }}>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Serif+Display&display=swap" rel="stylesheet" />

      {/* ── HEADER ── */}
      <header style={{
        padding: isMobile ? '16px 20px' : '20px 32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.gray100}`,
      }}>
        <div 
          onClick={handleLearnMore}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            cursor: 'pointer',
          }}
        >
          <div style={{
            width: '44px', height: '44px', background: '#fff', borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${colors.gray200}`,
          }}>
            <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: colors.gray900 }}>Tee Time Organizer</div>
            <div style={{ fontSize: '11px', color: colors.gray500, letterSpacing: '0.5px' }}>GOLF GROUP MANAGEMENT</div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '780px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 32px' }}>
        
        {/* ── SECTION 1: HERO ── */}
        <section style={{ 
          textAlign: 'center', 
          padding: isMobile ? '32px 0 28px' : '48px 0 36px',
        }}>
          {loading ? (
            <div style={{ color: colors.gray500, fontSize: '16px', padding: '40px 0' }}>Loading...</div>
          ) : (
            <>
              {/* Association badge or referral badge */}
              {isAssociation ? (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: colors.green50, color: colors.green800,
                  padding: '10px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 600,
                  marginBottom: '20px', border: `1px solid ${colors.green100}`,
                }}>
                  🤝 {config.shortName} Member Benefit
                </div>
              ) : (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: colors.green100, color: colors.green800,
                  padding: '10px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 600,
                  marginBottom: '20px', border: `1px solid ${colors.green200}`,
                }}>
                  🎉 You've Been Invited!
                </div>
              )}

              {/* Headline */}
              <h1 style={{
                fontFamily: "'DM Serif Display', Georgia, serif",
                fontSize: isMobile ? '28px' : '40px',
                fontWeight: 400,
                color: colors.gray900,
                lineHeight: 1.2,
                marginBottom: '16px',
              }}>
                {isAssociation ? (
                  <>
                    Stop chasing RSVPs.<br />
                    <span style={{ color: colors.green700 }}>Start organizing golf.</span>
                  </>
                ) : (
                  <>
                    {referrerName} invited you to join<br />
                    <span style={{ color: colors.green700 }}>Tee Time Organizer</span>
                  </>
                )}
              </h1>

              {/* Subtitle */}
              <p style={{ 
                fontSize: isMobile ? '15px' : '17px', 
                color: colors.gray600, 
                lineHeight: 1.6,
                maxWidth: '560px',
                margin: '0 auto',
              }}>
                {isAssociation
                  ? config.description
                  : referrerOrg
                    ? `${referrerName} from ${referrerOrg} thinks you'd love Tee Time Organizer — the app that replaces email chains and text blasts for golf group organizers.`
                    : 'The app that replaces email chains and text blasts for golf group organizers. Publish tee times, collect RSVPs, and manage your group — all in one place.'
                }
              </p>
            </>
          )}
        </section>

        {/* ── SECTION 2: HOW IT WORKS ── */}
        <section style={{ 
          padding: isMobile ? '28px 0' : '36px 0',
          borderTop: `1px solid ${colors.gray100}`,
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.gray900,
            textAlign: 'center',
            marginBottom: '28px',
          }}>
            How It Works
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '16px',
          }}>
            {howItWorks.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '18px',
                background: colors.gray50,
                borderRadius: '12px',
                border: `1px solid ${colors.gray100}`,
              }}>
                <div style={{
                  width: '36px', height: '36px', minWidth: '36px',
                  borderRadius: '50%',
                  background: colors.green700,
                  color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '15px', fontWeight: 700,
                }}>
                  {item.step}
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: colors.gray900, marginBottom: '4px' }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: '13px', color: colors.gray600, lineHeight: 1.5 }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 3: FEATURES ── */}
        <section style={{ 
          padding: isMobile ? '28px 0' : '36px 0',
          borderTop: `1px solid ${colors.gray100}`,
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.gray900,
            textAlign: 'center',
            marginBottom: '8px',
          }}>
            Everything You Need to Run Your Golf Group
          </h2>
          <p style={{
            fontSize: '15px',
            color: colors.gray500,
            textAlign: 'center',
            marginBottom: '28px',
          }}>
            Built by a golf organizer, for golf organizers
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '20px',
          }}>
            {featureCategories.map((cat, catIdx) => (
              <div key={catIdx} style={{
                background: 'white',
                borderRadius: '14px',
                border: `1px solid ${colors.gray200}`,
                padding: '24px',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  marginBottom: '18px',
                }}>
                  <span style={{ fontSize: '24px' }}>{cat.icon}</span>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: colors.gray900, margin: 0 }}>
                    {cat.title}
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {cat.features.map((feat, featIdx) => (
                    <div key={featIdx} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                    }}>
                      <span style={{ 
                        color: colors.green600, fontSize: '14px', fontWeight: 700, 
                        marginTop: '2px', minWidth: '16px',
                      }}>✓</span>
                      <span style={{ fontSize: '14px', color: colors.gray700, lineHeight: 1.5 }}>
                        {feat}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 4: SEE IT IN ACTION ── */}
        <section style={{ 
          padding: isMobile ? '28px 0' : '36px 0',
          borderTop: `1px solid ${colors.gray100}`,
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.gray900,
            textAlign: 'center',
            marginBottom: '8px',
          }}>
            See It in Action
          </h2>
          <p style={{
            fontSize: '15px',
            color: colors.gray500,
            textAlign: 'center',
            marginBottom: '24px',
          }}>
            Try the live demo — no signup required
          </p>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '16px',
            justifyContent: 'center',
          }}>
            <button
              onClick={handleViewDemo}
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '12px',
                border: `2px solid ${colors.green700}`,
                background: 'white',
                color: colors.green700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = colors.green50;
                e.currentTarget.style.borderColor = colors.green800;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = colors.green700;
              }}
            >
              ▶ Try the Live Demo
            </button>
            <button
              onClick={handleLearnMore}
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '12px',
                border: `2px solid ${colors.gray300}`,
                background: 'white',
                color: colors.gray700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = colors.gray50;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              Visit Full Website →
            </button>
          </div>
        </section>

        {/* ── SECTION 5: PRICING OFFER ── */}
        <section style={{ 
          padding: isMobile ? '28px 0' : '36px 0',
          borderTop: `1px solid ${colors.gray100}`,
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.green50} 0%, white 100%)`,
            border: `2px solid ${colors.green600}`,
            borderRadius: '16px',
            padding: isMobile ? '28px 20px' : '36px 32px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Decorative */}
            <div style={{
              position: 'absolute', top: '-30px', right: '-30px',
              width: '100px', height: '100px', background: colors.green100,
              borderRadius: '50%', opacity: 0.5,
            }} />
            <div style={{
              position: 'absolute', bottom: '-20px', left: '-20px',
              width: '70px', height: '70px', background: colors.green100,
              borderRadius: '50%', opacity: 0.3,
            }} />
            
            <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
              {/* Badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: colors.green800, color: 'white',
                padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 700,
                marginBottom: '20px', letterSpacing: '0.5px',
              }}>
                🎉 You've Been Invited
              </div>

              {/* Price */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ 
                  fontSize: isMobile ? '48px' : '56px', 
                  fontWeight: 800, 
                  color: colors.gray900,
                  lineHeight: 1,
                }}>
                  Free
                </span>
              </div>
              
              <div style={{ 
                fontSize: '16px', color: colors.gray700, fontWeight: 500, marginBottom: '6px',
              }}>
                Free for groups up to 8 players — no credit card required
              </div>
              <div style={{ 
                fontSize: '14px', color: colors.gray500, marginBottom: '24px',
              }}>
                Paid plans start at just $4.99/mo as your group grows
              </div>

              {/* CTA */}
              <div>
                <button
                  onClick={handleGetStarted}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    padding: '16px 48px',
                    fontSize: '17px',
                    fontWeight: 700,
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${colors.green600} 0%, ${colors.green800} 100%)`,
                    color: 'white',
                    boxShadow: '0 4px 14px rgba(22, 101, 52, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(22, 101, 52, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(22, 101, 52, 0.3)';
                  }}
                >
                  Get Started — Create Your Account
                  <span style={{ fontSize: '18px' }}>→</span>
                </button>
              </div>

              {isAssociation && (
                <p style={{ 
                  fontSize: '13px', color: colors.gray500, marginTop: '16px',
                  fontStyle: 'italic',
                }}>
                  This rate is available exclusively to {config.name} members
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: TRUST SIGNALS ── */}
        <section style={{ 
          padding: isMobile ? '24px 0 32px' : '32px 0 48px',
          textAlign: 'center',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: isMobile ? '20px' : '40px',
            flexWrap: 'wrap',
            marginBottom: '16px',
          }}>
            {[
              { icon: '🔒', text: 'Secure payments via Stripe' },
              { icon: '📱', text: 'Works on any device' },
              { icon: '🚫', text: 'No app download required for players' },
            ].map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '13px', color: colors.gray600,
              }}>
                <span>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '13px', color: colors.gray500 }}>
            Questions? Contact us at <a href="mailto:support@teetimeorganizer.net" style={{ color: colors.green700 }}>support@teetimeorganizer.net</a>
          </p>
        </section>
      </main>

      {/* ── FOOTER ── */}
      <footer style={{
        padding: '20px',
        textAlign: 'center',
        borderTop: `1px solid ${colors.gray200}`,
        color: colors.gray500,
        fontSize: '12px',
      }}>
        © {new Date().getFullYear()} Tee Time Organizer &nbsp;•&nbsp; 
        <span onClick={handleLearnMore} style={{ cursor: 'pointer', color: colors.green700 }}>
          teetimeorganizer.net
        </span>
      </footer>
    </div>
  );
};

export default WelcomePage;
